// Axios HTTP client for v2 dashboard.
//
// Base URL derived from window.location.hostname per
// runtime-base-urls.deriveApiBase() — same bundle serves all brand variants.
// Auth token injected via interceptor (reads from AuthContext when populated;
// no-op while CHG-1167 stub is in place).

import axios, { type AxiosInstance } from "axios";
import { deriveApiBase } from "../lib/runtime-base-urls";
import { getActiveOrgId } from "../contexts/active-org-storage";

let _client: AxiosInstance | null = null;

export function apiClient(): AxiosInstance {
  if (_client) return _client;
  _client = axios.create({
    baseURL: deriveApiBase() || "/api/v1",
    timeout: 30_000,
    headers: { "Content-Type": "application/json" },
  });
  // Read both authentication and organization selection at request time. The
  // client is a singleton, so capturing either value at construction would
  // keep stale scope after login or an OrgSwitcher transition.
  _client.interceptors.request.use((cfg) => {
    const token = window.localStorage.getItem("auth_token_dev"); // dev-only
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
      const activeOrgId = getActiveOrgId();
      // AxiosHeaders performs case-insensitive lookup. Preserve explicit
      // per-request overrides used by narrowly scoped administrative calls.
      if (activeOrgId && !cfg.headers.has("X-Org-Context")) {
        cfg.headers.set("X-Org-Context", activeOrgId);
      }
    }
    return cfg;
  });
  // CHG-1289 — defense-in-depth 403 interceptor. The FE usePermissions
  // hook gates affordances client-side as a UX optimization; if the
  // user has stale permissions and the BE returns 403, dispatch a
  // window CustomEvent so a toast / banner / page can surface it.
  // Same pattern as CHG-1287's window-CustomEvent telemetry; consumers
  // listen at window level (no component-coupled imports).
  _client.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 403 && typeof window !== "undefined") {
        try {
          window.dispatchEvent(
            new CustomEvent("v2-api-forbidden", {
              detail: {
                method: error?.config?.method,
                url: error?.config?.url,
                detail: error?.response?.data?.detail,
              },
            }),
          );
        } catch {
          // best-effort; never block error propagation
        }
      }
      // CHG-2916 / ITER-905655 — 401 handling. v1 (api.ts) runs a
      // single-flight refresh-and-retry against POST /auth/refresh, but
      // that requires a stored refresh_token. v2's AuthContext only
      // persists the access_token (under `auth_token_dev`) and discards
      // the refresh_token the BE returns, so there is no token to refresh
      // with here. Per the agreed contract: when a refresh path isn't
      // wired, fail the session CLEANLY — clear the stale token and
      // redirect to /login — instead of leaving the SPA wedged on dead
      // 401s ~60min after login. A `requestForceLogout()` helper (below)
      // de-dups concurrent 401s so N in-flight requests trigger exactly
      // ONE redirect.
      //
      // /auth/* 401s are real credential failures (e.g. a bad login) —
      // the LoginPage surfaces those itself; force-logout-on-401 there
      // would clobber the error UX and could loop. So those are skipped.
      //
      // CHG-2942 — the contact-change OTP confirm endpoints are also
      // skipped: a WRONG verification code returns 401 by design
      // (OtpService raises UnauthorizedError "Invalid code"), which is a
      // code failure inside an authenticated dialog, NOT a dead session.
      // Without this exemption a donor who mistypes the 6-digit code is
      // logged out and dumped on /login mid-flow.
      if (status === 401 && typeof window !== "undefined") {
        const url: string = error?.config?.url ?? "";
        const isAuthEndpoint = url.includes("/auth/");
        const isOtpConfirmEndpoint = /\/me\/confirm-(phone|email)-change$/.test(url);
        if (!isAuthEndpoint && !isOtpConfirmEndpoint) {
          requestForceLogout();
        }
      }
      throw error;
    },
  );
  return _client;
}

// CHG-2916 / ITER-905655 — single-flight force-logout. When a session's
// access token expires, every in-flight request 401s in the same tick;
// without de-dup that would fire N redirects / N token clears. The
// module-level guard ensures the clear + redirect runs once. Exported
// (test-only) so the 401-interceptor test can assert the behavior and
// reset the guard between cases.
let _loggingOut = false;

export function requestForceLogout(): void {
  if (_loggingOut) return;
  _loggingOut = true;
  try {
    window.localStorage.removeItem("auth_token_dev");
  } catch {
    // localStorage unavailable (private mode) — best-effort.
  }
  // Hard redirect (the api client sits below the router; we can't
  // useNavigate here — same constraint AuthContext.logout() documents).
  // Guard against redirect loops: don't bounce if already on /login.
  try {
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  } catch {
    // best-effort
  }
}

/** Test-only: reset the single-flight logout guard between cases. */
export function __resetForceLogoutGuard(): void {
  _loggingOut = false;
}
